import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { BeeLogo } from "../components/BeeLogo";
import { GlobalFooter } from "../components/GlobalFooter";
import { LanguagePicker } from "../components/LanguagePicker";
import { PasswordField } from "../components/PasswordField";
import { toast } from "sonner";
import t from "../lib/i18n";

export default function AuthPage() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register, language } = useApp();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let p;
      if (mode === "register") {
        p = await register(name, email, password);
        toast.success(t(language, "authWelcomeIn"));
      } else {
        p = await login(email, password);
        toast.success(t(language, "authWelcomeBack"));
      }
      navigate(p.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(language, "authSoftError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bn-honeycomb flex flex-col px-6 py-10">
      <div className="absolute top-5 right-5 z-10">
        <LanguagePicker />
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md bn-card p-8 md:p-10 bn-fade" data-testid="auth-card">
          <Link to="/" className="flex items-center gap-3 mb-8 justify-center" data-testid="auth-logo-link">
            <BeeLogo size={48} variant="portrait" />
            <span className="display text-2xl font-semibold">BeeNest</span>
          </Link>
          <h1 className="display text-3xl font-semibold text-center mb-2">
            {mode === "register" ? t(language, "createAccount") : t(language, "welcome")}
          </h1>
          <p className="text-center mb-8" style={{ color: "var(--bn-text-secondary)" }}>
            {mode === "register" ? t(language, "authRegisterSub") : t(language, "authLoginSub")}
          </p>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="block text-sm font-semibold mb-2 ml-2">{t(language, "name")}</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full rounded-2xl px-5 py-3 outline-none"
                  style={{
                    background: "var(--bn-surface-alt)",
                    border: "2px solid var(--bn-border)",
                  }}
                  data-testid="auth-name-input"
                  placeholder={language === "es" ? "Tu nombre" : "Your name"}
                  autoComplete="name"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold mb-2 ml-2">{t(language, "email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-2xl px-5 py-3 outline-none"
                style={{
                  background: "var(--bn-surface-alt)",
                  border: "2px solid var(--bn-border)",
                }}
                data-testid="auth-email-input"
                placeholder={language === "es" ? "tu@colmena.bee" : "you@hive.bee"}
                autoComplete="email"
              />
            </div>
            <PasswordField
              label={t(language, "password")}
              value={password}
              onChange={setPassword}
              required
              minLength={mode === "register" ? 8 : 6}
              testId="auth-password-input"
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              language={language}
            />
            <button
              type="submit"
              disabled={busy}
              className="bn-pill bn-primary-bg w-full text-base"
              data-testid="auth-submit-button"
            >
              {busy ? "..." : mode === "register" ? t(language, "createAccount") : t(language, "signIn")}
            </button>
          </form>

          {mode === "login" && (
            <p className="text-center mt-4 text-sm">
              <Link
                to="/auth/forgot"
                className="font-semibold underline-offset-4 hover:underline"
                style={{ color: "var(--bn-text-secondary)" }}
                data-testid="auth-forgot-link"
              >
                {t(language, "forgotPasswordLink")}
              </Link>
            </p>
          )}

          <p className="text-center mt-6 text-sm" style={{ color: "var(--bn-text-secondary)" }}>
            {mode === "register"
              ? (language === "es" ? "¿Ya estás en la colmena? " : "Already in the hive? ")
              : (language === "es" ? "¿Nuevo aquí? " : "New here? ")}
            <button
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="font-semibold underline-offset-4 hover:underline"
              style={{ color: "var(--bn-accent-terracotta)" }}
              data-testid="auth-toggle-mode"
            >
              {mode === "register" ? t(language, "signIn") : t(language, "createAccount")}
            </button>
          </p>
        </div>
      </div>
      <GlobalFooter />
    </div>
  );
}
